// src/pages/services/ComingSoon.jsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Building2, Shield, Briefcase, TrendingUp, Info } from "lucide-react";

// Configuration des différents services
const serviceConfig = {
  "auction-houses": {
    icon: Building2,
    title: "Opportunités Maisons de Vente",
    subtitle: "Suivez les ventes aux enchères pertinentes pour votre collection",
    description: "Accédez en temps réel aux ventes aux enchères des plus grandes maisons internationales",
    requiresFamilySelection: false,
    features: [
      {
        title: "Alertes personnalisées",
        description: "Recevez des notifications lorsque des œuvres similaires à votre collection sont mises en vente."
      },
      {
        title: "Analyses comparatives",
        description: "Comparez les estimations et résultats de vente avec vos propres valorisations."
      },
      {
        title: "Calendrier global",
        description: "Visualisez toutes les ventes importantes : Christie's, Sotheby's, Artcurial, Phillips et plus."
      },
      {
        title: "Rapports détaillés",
        description: "Obtenez des analyses post-vente avec prix marteau, frais et tendances du marché."
      }
    ],
    color: "indigo"
  },
  "insurance": {
    icon: Shield,
    title: "Assurance & Couverture",
    subtitle: "Protégez votre patrimoine artistique",
    description: "Une solution complète pour l'assurance de collections d'art",
    requiresFamilySelection: false,
    features: [
      {
        title: "Évaluation automatique",
        description: "Calculez les besoins d'assurance basés sur vos valorisations actualisées."
      },
      {
        title: "Dossiers standardisés",
        description: "Générez automatiquement tous les documents requis par les assureurs."
      },
      {
        title: "Réseau de partenaires",
        description: "Accédez à nos assureurs partenaires spécialisés en œuvres d'art."
      },
      {
        title: "Suivi des polices",
        description: "Gérez vos contrats et renouvellements depuis votre tableau de bord."
      }
    ],
    color: "emerald"
  },
  "museum-loans": {
    icon: Briefcase,
    title: "Prêts Muséaux",
    subtitle: "Partagez votre collection avec le monde",
    description: "Facilitez les prêts de vos œuvres aux institutions culturelles",
    requiresFamilySelection: false,
    features: [
      {
        title: "Matching intelligent",
        description: "Recevez des propositions de prêts pertinentes pour votre collection."
      },
      {
        title: "Gestion documentaire",
        description: "Générez facility reports et documents de prêt standardisés."
      },
      {
        title: "Suivi logistique",
        description: "Coordonnez transport, assurance et installation avec les musées."
      },
      {
        title: "Valorisation culturelle",
        description: "Augmentez la visibilité et la valeur culturelle de votre collection."
      }
    ],
    color: "purple"
  },
  "fiscal-optimization": {
    icon: TrendingUp,
    title: "Planification Fiscale",
    subtitle: "Planifiez la fiscalité de votre patrimoine artistique",
    description: "Des outils pour anticiper et gérer la fiscalité de votre collection",
    requiresFamilySelection: true,
    legalNotice: "Ce service propose des outils d'analyse et de simulation. Il ne constitue pas un conseil fiscal personnalisé. Pour toute décision fiscale, veuillez consulter un expert-comptable ou un fiscaliste agréé.",
    features: [
      {
        title: "Planification fiscale",
        description: "Simulez différents scénarios de donation, vente ou succession."
      },
      {
        title: "Conformité multi-juridictions",
        description: "Gérez la fiscalité dans plusieurs pays européens."
      },
      {
        title: "Documentation automatisée",
        description: "Générez les déclarations et justificatifs nécessaires."
      },
      {
        title: "Conseil expert",
        description: "Connectez-vous avec nos fiscalistes spécialisés en art."
      }
    ],
    color: "amber"
  }
};

export default function ComingSoon() {
  const location = useLocation();
  const [selectedFamily, setSelectedFamily] = useState("");
  const [families, setFamilies] = useState([]);
  
  // Déterminer quel service afficher depuis les query params
  const params = new URLSearchParams(location.search);
  const serviceType = params.get("service") || "auction-houses";
  const config = serviceConfig[serviceType] || serviceConfig["auction-houses"];
  
  const IconComponent = config.icon;

  // Charger les familles si nécessaire
  useEffect(() => {
    if (config.requiresFamilySelection) {
      loadFamilies();
    }
  }, [config.requiresFamilySelection]);

  const loadFamilies = async () => {
    try {
      // Note: À adapter selon votre configuration Supabase
      // const { data, error } = await supabase
      //   .from("families")
      //   .select("id, family_name")
      //   .order("family_name");
      
      // if (error) throw error;
      
      // Mock data pour démonstration
      const mockFamilies = [
        { id: "1", family_name: "Famille Martin" },
        { id: "2", family_name: "Famille Dupont" },
        { id: "3", family_name: "Famille Bernard" }
      ];
      
      setFamilies(mockFamilies);
      if (mockFamilies.length > 0) {
        setSelectedFamily(mockFamilies[0].id);
      }
    } catch (error) {
      console.error("Erreur chargement familles:", error);
    }
  };
  
  // Configuration des couleurs par type
  const colorClasses = {
    indigo: {
      bg: "bg-indigo-50",
      border: "border-indigo-200",
      text: "text-indigo-700",
      icon: "text-indigo-400",
      badge: "bg-indigo-100 text-indigo-700",
      button: "bg-indigo-600 hover:bg-indigo-700",
      gradient: "from-indigo-50 to-purple-50"
    },
    emerald: {
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      text: "text-emerald-700",
      icon: "text-emerald-400",
      badge: "bg-emerald-100 text-emerald-700",
      button: "bg-emerald-600 hover:bg-emerald-700",
      gradient: "from-emerald-50 to-teal-50"
    },
    purple: {
      bg: "bg-purple-50",
      border: "border-purple-200",
      text: "text-purple-700",
      icon: "text-purple-400",
      badge: "bg-purple-100 text-purple-700",
      button: "bg-purple-600 hover:bg-purple-700",
      gradient: "from-purple-50 to-pink-50"
    },
    amber: {
      bg: "bg-amber-50",
      border: "border-amber-200",
      text: "text-amber-700",
      icon: "text-amber-400",
      badge: "bg-amber-100 text-amber-700",
      button: "bg-amber-600 hover:bg-amber-700",
      gradient: "from-amber-50 to-orange-50"
    }
  };
  
  const colors = colorClasses[config.color];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header simple et élégant */}
      <div className="bg-white border-b border-[#E8E8E8]">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <Link 
            to="/family-office/dashboard" 
            className="inline-flex items-center gap-2 text-sm text-[#666] hover:text-[#1A1A1A] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au tableau de bord
          </Link>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-5xl mx-auto px-6 py-20">
        {/* Hero section */}
        <div className="text-center mb-16">
          <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full ${colors.bg} mb-8`}>
            <IconComponent className={`w-12 h-12 ${colors.icon}`} />
          </div>
          
          <h1 className="text-5xl font-serif text-[#1A1A1A] mb-4 tracking-tight">
            {config.title}
          </h1>
          
          <p className="text-xl text-[#666] mb-8 max-w-2xl mx-auto font-light">
            {config.subtitle}
          </p>

          <div className={`inline-flex items-center gap-2 px-5 py-2.5 ${colors.badge} rounded-full text-sm font-medium`}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Disponible au T2 2025
          </div>
        </div>

        {/* Sélecteur de famille si nécessaire */}
        {config.requiresFamilySelection && families.length > 0 && (
          <div className="bg-white rounded-xl border border-[#E8E8E8] p-6 mb-10 shadow-sm">
            <label className="block text-sm text-[#666] mb-3 font-light">
              Sélectionnez la famille concernée
            </label>
            <select
              value={selectedFamily}
              onChange={(e) => setSelectedFamily(e.target.value)}
              className="w-full px-4 py-3 border border-[#E8E8E8] rounded-lg bg-white text-[#1A1A1A] font-light focus:outline-none focus:ring-2 focus:ring-[#1A1A1A] focus:border-transparent"
            >
              {families.map(family => (
                <option key={family.id} value={family.id}>
                  {family.family_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Note légale si nécessaire */}
        {config.legalNotice && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-10">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-serif text-[#1A1A1A] mb-2 text-sm">Note importante</h4>
                <p className="text-sm text-[#666] font-light leading-relaxed">
                  {config.legalNotice}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Description du service */}
        <div className="bg-white rounded-2xl border border-[#E8E8E8] p-10 mb-10 shadow-sm">
          <p className="text-lg text-[#666] leading-relaxed mb-10 text-center font-light">
            {config.description}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {config.features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#F5F5F5] flex items-center justify-center">
                  <span className="text-[#1A1A1A] font-serif text-sm">{idx + 1}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-[#1A1A1A] mb-2 text-lg">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-[#666] leading-relaxed font-light">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA élégant */}
        <div className={`bg-gradient-to-br ${colors.gradient} rounded-2xl border ${colors.border} p-12 text-center shadow-sm`}>
          <h3 className="text-2xl font-serif text-[#1A1A1A] mb-4">
            Soyez informé du lancement
          </h3>
          <p className="text-[#666] mb-8 max-w-md mx-auto font-light">
            Inscrivez-vous pour être notifié dès que ce service sera disponible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="votre@email.com"
              className="flex-1 px-5 py-3.5 rounded-lg border border-[#D0D0D0] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A] focus:border-transparent bg-white text-[#1A1A1A] font-light"
            />
            <button className={`px-8 py-3.5 ${colors.button} text-white rounded-lg transition-colors font-medium shadow-sm`}>
              Me notifier
            </button>
          </div>
        </div>

        {/* Navigation retour */}
        <div className="text-center mt-16">
          <Link 
            to="/family-office/dashboard" 
            className="inline-flex items-center gap-2 text-sm text-[#666] hover:text-[#1A1A1A] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à ma collection
          </Link>
        </div>
      </div>
    </div>
  );
}